import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, push, set, update, remove } from 'firebase/database';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ReportAnalysis.css';

const LoanAccountLedger = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [viewingLoan, setViewingLoan] = useState(null);
  const [entries, setEntries] = useState([
    {
      id: Date.now(),
      loanAccountName: '',
      lenderName: '',
      fromDate: new Date().toISOString().split('T')[0],
      toDate: '',
      openingBalance: '',
      emiInstallmentPaid: '',
      interestAmount: '',
      principalAmount: '',
      closingBalance: ''
    }
  ]);
  const [expandedEntry, setExpandedEntry] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Auto-calculate closing balance for all entries
  useEffect(() => {
    setEntries(prev => prev.map(entry => {
      const opening = parseFloat(entry.openingBalance) || 0;
      const emi = parseFloat(entry.emiInstallmentPaid) || 0;
      const closing = opening - emi;
      return {
        ...entry,
        closingBalance: closing.toFixed(2)
      };
    }));
  }, []);

  useEffect(() => {
    fetchLoans();
  }, []);

  const fetchLoans = async () => {
    try {
      const db = getDatabase();
      const loansRef = ref(db, 'HTAMS/expenses/loanaccountledger');
      const snapshot = await get(loansRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const loansList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setLoans(loansList);
      } else {
        setLoans([]);
      }
    } catch (error) {
      console.error('Error fetching loans:', error);
      toast.error('Failed to fetch loan data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    
    // Validation for amount and rate fields
    if (name === 'openingBalance' || name === 'emiInstallmentPaid' || name === 'interestAmount' || name === 'principalAmount') {
      // Only allow numbers and decimal point
      const numericValue = value.replace(/[^\d.]/g, '');
      // Prevent multiple decimal points
      const parts = numericValue.split('.');
      const validValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
      
      setEntries(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [name]: validValue };
        
        // Auto-calculate closing balance
        if (name === 'openingBalance' || name === 'emiInstallmentPaid') {
          const opening = parseFloat(name === 'openingBalance' ? validValue : updated[index].openingBalance) || 0;
          const emi = parseFloat(name === 'emiInstallmentPaid' ? validValue : updated[index].emiInstallmentPaid) || 0;
          updated[index].closingBalance = (opening - emi).toFixed(2);
        }
        
        return updated;
      });
      return;
    }

    setEntries(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  const addNewEntry = () => {
    const newEntry = {
      id: Date.now(),
      loanAccountName: '',
      lenderName: '',
      fromDate: new Date().toISOString().split('T')[0],
      toDate: '',
      openingBalance: '',
      emiInstallmentPaid: '',
      interestAmount: '',
      principalAmount: '',
      closingBalance: ''
    };
    setEntries([...entries, newEntry]);
    setExpandedEntry(entries.length);
  };

  const removeEntry = (index) => {
    if (entries.length > 1) {
      const updated = entries.filter((_, i) => i !== index);
      setEntries(updated);
      if (expandedEntry >= updated.length) {
        setExpandedEntry(updated.length - 1);
      }
    }
  };

  const toggleEntry = (index) => {
    setExpandedEntry(expandedEntry === index ? -1 : index);
  };

  const validateEntry = (entry, index) => {
    const entryNum = index + 1;
    if (!entry.loanAccountName || !entry.loanAccountName.trim()) {
      toast.error(`Entry ${entryNum}: Loan account name is required`);
      return false;
    }
    if (!entry.lenderName || !entry.lenderName.trim()) {
      toast.error(`Entry ${entryNum}: Lender name is required`);
      return false;
    }
    if (!entry.fromDate) {
      toast.error(`Entry ${entryNum}: From date is required`);
      return false;
    }
    if (!entry.openingBalance || entry.openingBalance.trim() === '') {
      toast.error(`Entry ${entryNum}: Opening balance is required`);
      return false;
    }
    if (parseFloat(entry.openingBalance) <= 0) {
      toast.error(`Entry ${entryNum}: Opening balance must be greater than 0`);
      return false;
    }
    if (!entry.emiInstallmentPaid || entry.emiInstallmentPaid.trim() === '') {
      toast.error(`Entry ${entryNum}: EMI/Installment paid is required`);
      return false;
    }
    if (parseFloat(entry.emiInstallmentPaid) < 0) {
      toast.error(`Entry ${entryNum}: EMI/Installment paid cannot be negative`);
      return false;
    }
    if (parseFloat(entry.emiInstallmentPaid) > parseFloat(entry.openingBalance)) {
      toast.error(`Entry ${entryNum}: EMI paid cannot be greater than opening balance`);
      return false;
    }
    if (!entry.interestAmount || parseFloat(entry.interestAmount) < 0 || parseFloat(entry.interestAmount) > 100) {
      toast.error(`Entry ${entryNum}: Interest rate must be between 0 and 100`);
      return false;
    }
    if (entry.toDate && new Date(entry.toDate) < new Date(entry.fromDate)) {
      toast.error(`Entry ${entryNum}: To date cannot be earlier than from date`);
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setEntries([
      {
        id: Date.now(),
        loanAccountName: '',
        lenderName: '',
        fromDate: new Date().toISOString().split('T')[0],
        toDate: '',
        openingBalance: '',
        emiInstallmentPaid: '',
        interestAmount: '',
        principalAmount: '',
        closingBalance: ''
      }
    ]);
    setExpandedEntry(0);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all entries
    for (let i = 0; i < entries.length; i++) {
      if (!validateEntry(entries[i], i)) {
        return;
      }
    }
    
    setSubmitting(true);

    try {
      const db = getDatabase();
      const loansRef = ref(db, 'HTAMS/expenses/loanaccountledger');

      if (editingId) {
        // Update single entry
        const loanRef = ref(db, `HTAMS/expenses/loanaccountledger/${editingId}`);
        const { id, ...entryData } = entries[0];
        await update(loanRef, entryData);
        toast.success('Loan account updated successfully!');
      } else {
        // Create multiple entries
        const savePromises = entries.map(entry => {
          const { id, ...entryData } = entry;
          const newLoanRef = push(loansRef);
          return set(newLoanRef, entryData);
        });
        await Promise.all(savePromises);
        toast.success(`${entries.length} Loan Account${entries.length > 1 ? 's' : ''} added successfully!`);
      }

      resetForm();
      fetchLoans();
    } catch (error) {
      console.error('Error saving loan:', error);
      toast.error('Failed to save loan account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (loan) => {
    setEntries([
      {
        id: Date.now(),
        loanAccountName: loan.loanAccountName,
        lenderName: loan.lenderName,
        fromDate: loan.fromDate,
        toDate: loan.toDate,
        openingBalance: loan.openingBalance,
        emiInstallmentPaid: loan.emiInstallmentPaid,
        interestAmount: loan.interestAmount,
        principalAmount: loan.principalAmount,
        closingBalance: loan.closingBalance
      }
    ]);
    setExpandedEntry(0);
    setEditingId(loan.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete loan account "${name}"?`)) {
      try {
        const db = getDatabase();
        const loanRef = ref(db, `HTAMS/expenses/loanaccountledger/${id}`);
        await remove(loanRef);
        toast.success('Loan account deleted successfully!');
        fetchLoans();
      } catch (error) {
        console.error('Error deleting loan:', error);
        toast.error('Failed to delete loan account');
      }
    }
  };

  const handleView = (loan) => {
    setViewingLoan(loan);
  };

  const closeViewModal = () => {
    setViewingLoan(null);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const filteredLoans = loans.filter(loan => {
    const matchesSearch = loan.loanAccountName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         loan.lenderName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = (!dateFilter.start || new Date(loan.fromDate) >= new Date(dateFilter.start)) &&
                       (!dateFilter.end || new Date(loan.fromDate) <= new Date(dateFilter.end));
    return matchesSearch && matchesDate;
  });

  const totalOutstanding = filteredLoans.reduce((sum, loan) => sum + (parseFloat(loan.closingBalance) || 0), 0);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLoans = filteredLoans.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLoans.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const exportToExcel = () => {
    if (filteredLoans.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Prepare data for export
    const exportData = filteredLoans.map((loan, index) => ({
      'Sr No': index + 1,
      'Loan Account Name': loan.loanAccountName || '-',
      'Lender Name': loan.lenderName || '-',
      'From Date': formatDate(loan.fromDate),
      'To Date': formatDate(loan.toDate) || '-',
      'Opening Balance': loan.openingBalance,
      'EMI Paid': loan.emiInstallmentPaid,
      'Interest Rate (%)': loan.interestAmount,
      'Principal Amount': loan.principalAmount,
      'Closing Balance': loan.closingBalance
    }));

    // Convert to CSV
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in values
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Loan_Account_Ledger_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredLoans.length} entries successfully!`);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter]);

  if (loading) return <div className="loading">Loading loan accounts...</div>;

  return (
    <div className="report-analysis-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="page-header">
        <h2>Loan Account Ledger</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="add-btn"
            onClick={exportToExcel}
            style={{ background: '#28a745' }}
          >
            📥 Export to Excel
          </button>
          <button 
            className="add-btn"
            onClick={() => {
              if (showForm && editingId) {
                handleCancelEdit();
              } else {
                setShowForm(!showForm);
              }
            }}
          >
            {showForm ? '✕ Close' : '+ Add Loan Account'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="form-container">
          <h3 className="form-title">{editingId ? 'Edit Loan Account' : 'Add New Loan Account'}</h3>
          <form onSubmit={handleSubmit} className="purchase-form">
            {entries.map((entry, index) => (
              <div key={entry.id} style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                <div 
                  style={{
                    background: expandedEntry === index ? '#2c5f2d' : '#f8f9fa',
                    color: expandedEntry === index ? 'white' : '#333',
                    padding: '12px 15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontWeight: '500'
                  }}
                >
                  <span 
                    onClick={() => toggleEntry(index)}
                    style={{ cursor: 'pointer', flex: 1 }}
                  >
                    Entry {index + 1} {entry.loanAccountName && `- ${entry.loanAccountName}`} {entry.openingBalance && `(₹${entry.openingBalance})`}
                  </span>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {!editingId && entries.length > 1 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeEntry(index);
                        }}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        Remove
                      </button>
                    )}
                    <span 
                      onClick={() => toggleEntry(index)}
                      style={{ cursor: 'pointer', fontSize: '20px' }}
                    >
                      {expandedEntry === index ? '−' : '+'}
                    </span>
                  </div>
                </div>
                
                {expandedEntry === index && (
                  <div style={{ padding: '20px' }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(5, 1fr)', 
                      gap: '15px',
                      marginBottom: '15px'
                    }}>
                      <div className="form-group">
                        <label>Loan Account Name <span className="required">*</span></label>
                        <input
                          type="text"
                          name="loanAccountName"
                          value={entry.loanAccountName}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter loan account name"
                          required
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Lender Name <span className="required">*</span></label>
                        <input
                          type="text"
                          name="lenderName"
                          value={entry.lenderName}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter lender name"
                          required
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>From Date <span className="required">*</span></label>
                        <input
                          type="date"
                          name="fromDate"
                          value={entry.fromDate}
                          onChange={(e) => handleInputChange(e, index)}
                          required
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>To Date</label>
                        <input
                          type="date"
                          name="toDate"
                          value={entry.toDate}
                          onChange={(e) => handleInputChange(e, index)}
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Opening Balance <span className="required">*</span></label>
                        <input
                          type="number"
                          name="openingBalance"
                          value={entry.openingBalance}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter opening balance"
                          required
                          step="0.01"
                          min="0"
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>EMI / Installment Paid <span className="required">*</span></label>
                        <input
                          type="number"
                          name="emiInstallmentPaid"
                          value={entry.emiInstallmentPaid}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter EMI amount"
                          required
                          step="0.01"
                          min="0"
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Interest Amount (%) <span className="required">*</span></label>
                        <input
                          type="number"
                          name="interestAmount"
                          value={entry.interestAmount}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter interest rate"
                          required
                          step="0.01"
                          min="0"
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Principal Amount <span className="required">*</span></label>
                        <input
                          type="number"
                          name="principalAmount"
                          value={entry.principalAmount}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter principal amount"
                          required
                          step="0.01"
                          min="0"
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Closing Balance</label>
                        <input
                          type="number"
                          name="closingBalance"
                          value={entry.closingBalance}
                          readOnly
                          className="form-control"
                          style={{backgroundColor: '#f0f0f0', cursor: 'not-allowed'}}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {!editingId && (
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <button
                  type="button"
                  onClick={addNewEntry}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    display: 'inline-block'
                  }}
                >
                  + Add Another Entry
                </button>
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button" 
                onClick={handleCancelEdit}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={submitting}
                className="btn-submit"
              >
                {submitting ? 'Saving...' : (editingId ? 'Update Loan Account' : `Save ${entries.length} Loan Account${entries.length > 1 ? 's' : ''}`)}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters">
        <input
          type="text"
          placeholder="Search by loan account or lender name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ flex: '2' }}
        />
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Start Date</label>
          <input
            type="date"
            value={dateFilter.start}
            onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
            className="date-input"
          />
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>End Date</label>
          <input
            type="date"
            value={dateFilter.end}
            onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
            className="date-input"
          />
        </div>
        
        {(searchTerm || dateFilter.start || dateFilter.end) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setDateFilter({ start: '', end: '' });
            }}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total Outstanding</h3>
          <p>₹{totalOutstanding.toLocaleString()}</p>
        </div>
      </div>

      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Loan Account Name</th>
              <th>Lender Name</th>
              <th>Opening Balance</th>
              <th>EMI Paid</th>
              <th>Interest Amount</th>
              <th>Principal Amount</th>
              <th>Closing Balance</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentLoans.length > 0 ? (
              currentLoans.map((loan, index) => (
                <tr key={loan.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{loan.loanAccountName}</td>
                  <td>{loan.lenderName}</td>
                  <td>₹{parseFloat(loan.openingBalance)?.toLocaleString()}</td>
                  <td>₹{parseFloat(loan.emiInstallmentPaid)?.toLocaleString()}</td>
                  <td>{parseFloat(loan.interestAmount)?.toFixed(2)}%</td>
                  <td>₹{parseFloat(loan.principalAmount)?.toLocaleString()}</td>
                  <td>₹{parseFloat(loan.closingBalance)?.toLocaleString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn view-btn"
                        onClick={() => handleView(loan)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(loan)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(loan.id, loan.loanAccountName)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : null}
          </tbody>
        </table>
        
        {currentLoans.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">💳</div>
            <h3>No loan accounts found</h3>
            <p>Add loan accounts using the "Add Loan Account" button above.</p>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewingLoan && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Loan Account Details</h3>
              <button className="modal-close" onClick={closeViewModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Loan Account Name:</label>
                  <span>{viewingLoan.loanAccountName}</span>
                </div>
                <div className="detail-item">
                  <label>Lender Name:</label>
                  <span>{viewingLoan.lenderName}</span>
                </div>
                <div className="detail-item">
                  <label>From Date:</label>
                  <span>{formatDate(viewingLoan.fromDate)}</span>
                </div>
                <div className="detail-item">
                  <label>To Date:</label>
                  <span>{formatDate(viewingLoan.toDate) || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Opening Balance:</label>
                  <span className="highlight-amount">₹{parseFloat(viewingLoan.openingBalance)?.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>EMI / Installment Paid:</label>
                  <span>₹{parseFloat(viewingLoan.emiInstallmentPaid)?.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Interest Amount:</label>
                  <span>{parseFloat(viewingLoan.interestAmount)?.toFixed(2)}%</span>
                </div>
                <div className="detail-item">
                  <label>Principal Amount:</label>
                  <span>₹{parseFloat(viewingLoan.principalAmount)?.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Closing Balance:</label>
                  <span className="highlight-amount">₹{parseFloat(viewingLoan.closingBalance)?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredLoans.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredLoans.length)} of {filteredLoans.length} entries
          </div>
          <div className="pagination">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            {getPageNumbers().map(number => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`pagination-btn ${currentPage === number ? 'active' : ''}`}
              >
                {number}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanAccountLedger;
